import React, { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import moment from "moment";

import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import OutlinedInput from "@mui/material/OutlinedInput";

import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";

import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CommentIcon from "@mui/icons-material/Comment";

import TablePagination from "@mui/material/TablePagination";
import TableFooter from "@mui/material/TableFooter";

import Stack from "@mui/material/Stack";

import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DoneOutlineRounded from "@mui/icons-material/DoneOutlineRounded";
import ClearIcon from "@mui/icons-material/Clear";

import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";

import { useTheme } from "@mui/material/styles";
import Modal from "../../components/Modal";
import DeleteDialog from "../../components/DeleteDialog";

import api from "../../service/api";
import { setError, setSuccess } from "../../reducers/appSlice";
import VisorPdf from "../../components/Visorpdf";
import FirmaRemota from "../../commons/FirmaRemota";
import VerificarFirma from "../../commons/VerificarFirma";
import FirmaToken from "../../commons/FirmaToken";
import VerificarUltimoAdjunto from "../../commons/VerificarUltimoAdjunto";
import ModalNotificar from "../../commons/ModalNotificar";

import Accion from "../../components/Accion";

import { Editor } from "react-draft-wysiwyg";
import { EditorState, ContentState } from 'draft-js';
import { convertToRaw, convertFromHTML } from 'draft-js';
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from 'draftjs-to-html';

import FileInput from "./FileInput";

function stripHTMLTags(input) {
  if (!input) return "";
  return input.replace(/<\/?[^>]+(>|$)/g, '');
}

const notEmptyEditor = (value) => {
  const cleanString = stripHTMLTags(value).trim();
  return (cleanString !== "");
};

const TablePaginationActions = (props) => {
  const theme = useTheme();

  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
            <KeyboardArrowLeft />
          )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
            <KeyboardArrowRight />
          )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const Input = styled("input")({
  //display: 'none',
  fontFamily: ['"Open Sans"', "Arial"].join(","),
  fontSize: "15.5px",
  color: "#9e9e9e",
});

// const validationSchema = Yup.object().shape({
//   idDecision: Yup.number().required("Requerido"),
//   archivoAdjunto: Yup.string().required("Requerido"),
//   informe: Yup.string().test('no-html', 'Requerido', notEmptyEditor)
// });

const validationSchema = Yup.object().shape({
  idDecision: Yup.number().required("Requerido"),
  archivoAdjunto: Yup.mixed().test('file-required', 'Requerido', function (value) {
    const { informe } = this.parent;
    if (!informe || informe.trim() === '') {
      return !!value || this.createError({ message: 'Requerido' });
    }
    return true;
  }).nullable(),
  informe: Yup.string().test('no-html', 'Informe no puede estar vacío', function (value) {
    const { archivoAdjunto } = this.parent;
    if (!archivoAdjunto) {
      return !!value || this.createError({ message: 'Requerido' });
    }
    return true;
  }).nullable()
});

const Adjuntos = (props) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [iniciadoresSeleccionados, setIniciadoresSeleccionados] = useState([]);
  const [documento, setDocumento] = useState("");
  const [observacion, setObservacion] = useState("");
  const [formatoSeleccionado, setFormatoSeleccionado] = useState("");
  const [tienePermisoAgregarAdjunto, setTienePermisoAgregarAdjunto] = useState("N");
  const [nombreArchivoSeleccionado, setNombreArchivoSeleccionado] = useState("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [decisionesAdministrativas, setDecisionesAdministrativas] = useState([]);
  const [iniciadores, setIniciadores] = useState([]);
  const [adjuntosTramite, setAdjuntosTramite] = useState([]);
  const [open, setOpen] = React.useState(false);
  const [openNuevoDocumento, setOpenNuevoDocumento] = React.useState(false);
  const [openObservacion, setOpenObservacion] = React.useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verifica, setVerifica] = useState(false);
  const [verificaSinfirma, setVerificaSinfirma] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const onEditorStateChange = editorState => {
    setEditorState(editorState);
    formik.setFieldValue("informe", draftToHtml(convertToRaw(editorState.getCurrentContent())));
    //setInforme(draftToHtml(convertToRaw(editorState.getCurrentContent())));
  };

  const verficarElFirmado = () => {
    setOpenNuevoDocumento(true);
    setVerifica(false);
  };

  const verficarElFirmadoCancelar = () => {
    setVerifica(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const definirFormato = (event) => {
    setNombreArchivoSeleccionado(event.target.value);
    if (event.target.value !== null && event.target.value !== "") {
      let documentoSeleccionado = event.target.value.split(".");
      let extension = documentoSeleccionado[1].toUpperCase();
      setFormatoSeleccionado(extension);
    } else {
      setFormatoSeleccionado("");
    }
  };

  function actualizarDatos() {
    buscarAdjuntosTramites();
  }


  const formik = useFormik({
    initialValues: {
      idDecision: "",
      archivoAdjunto: "",
      observacion: "",
      notificar: "false",
      informe: "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setSubmitting(true);
      let error = "";
      if (!notEmptyEditor(formik.values.informe)) {
        if (values.archivoAdjunto.size !== 0) {
          if (!validarDocumentoDecision()) {
            error = "El tipo de actuación administrativa seleccionado permite adjuntar solamente documentos en formato PDF.";
          }
        } else {
          error = "El archivo seleccionado se encuentra vacio. Verifique.";
        }
      }

      //   if (!validarDocumentoDecision()) {
      //     if (values.archivoAdjunto.size === 0) {
      //       // dispatch(
      //       //   setError("El archivo seleccionado se encuentra vacio. Verifique.")
      //       // );
      //       // setSubmitting(false);
      //       // console.log("error vacio")
      //       // return;
      //       error = "El archivo seleccionado se encuentra vacio. Verifique.";
      //     }
      //   } else {
      //     // dispatch(
      //     //   setError("El archivo seleccionado se encuentra vacio. Verifique.")
      //     // );
      //     // setSubmitting(false);
      //     // console.log("error tipo")
      //     // return;
      //     error = "El tipo de actuación administrativa seleccionado permite adjuntar solamente documentos en formato PDF.";
      //   }
      // }

      if (error) {
        dispatch(setError(error.message));
        setSubmitting(false);
      } else {
        let mailSeleccionados = [];

        for (let index = 0; index < iniciadoresSeleccionados.length; index++) {
          const iniciador = iniciadoresSeleccionados[index];
          let inicioMail = iniciador.indexOf("(");
          let finMail = iniciador.indexOf(")");
          let nombre = iniciador.substr(0, inicioMail);
          let mail = iniciador.substr(
            inicioMail + 1,
            finMail - inicioMail - 1
          );
          mailSeleccionados.push(nombre + "#" + mail);
        }

        let datosAdjunto = new FormData();
        datosAdjunto.append("tra_id", props.id);
        datosAdjunto.append("observacion", values.observacion);
        datosAdjunto.append("documento", values.archivoAdjunto);
        datosAdjunto.append("nombre_doc", nombreArchivoSeleccionado || "informe.pdf");
        datosAdjunto.append("decisiones_adm", values.idDecision);
        datosAdjunto.append("informe", values.informe);
        datosAdjunto.append(
          "notificacion",
          JSON.stringify(mailSeleccionados)
        );

        api
          .guardarAdjuntoTramite(datosAdjunto, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((res) => {
            dispatch(setSuccess("Archivo adjuntado con éxito"));
            setEditorState(EditorState.createEmpty());
            formik.handleReset();
            setSubmitting(false);
            setOpenNuevoDocumento(false);
          })
          .catch((e) => dispatch(setError("Error al intentar adjuntar archivo.")))
          .finally(() => setSubmitting(false));
      }

      // if (validarDocumentoDecision() === true) {
      //   if (values.archivoAdjunto.size === 0) {
      //     dispatch(
      //       setError("El archivo seleccionado se encuentra vacio. Verifique.")
      //     );
      //     setSubmitting(false);
      //   } else {
      //     setSubmitting(true);

      //     let mailSeleccionados = [];

      //     for (let index = 0; index < iniciadoresSeleccionados.length; index++) {
      //       const iniciador = iniciadoresSeleccionados[index];
      //       let inicioMail = iniciador.indexOf("(");
      //       let finMail = iniciador.indexOf(")");
      //       let nombre = iniciador.substr(0, inicioMail);
      //       let mail = iniciador.substr(
      //         inicioMail + 1,
      //         finMail - inicioMail - 1
      //       );
      //       mailSeleccionados.push(nombre + "#" + mail);
      //     }

      //     let datosAdjunto = new FormData();
      //     datosAdjunto.append("tra_id", props.id);
      //     datosAdjunto.append("observacion", values.observacion);
      //     datosAdjunto.append("documento", values.archivoAdjunto);
      //     datosAdjunto.append("nombre_doc", nombreArchivoSeleccionado);
      //     datosAdjunto.append("decisiones_adm", values.idDecision);
      //     datosAdjunto.append("informe", values.informe);
      //     datosAdjunto.append(
      //       "notificacion",
      //       JSON.stringify(mailSeleccionados)
      //     );

      //     api
      //       .guardarAdjuntoTramite(datosAdjunto, {
      //         headers: {
      //           "Content-Type": "multipart/form-data",
      //         },
      //       })
      //       .then((res) => {
      //         dispatch(setSuccess("Archivo adjuntado con éxito"));
      //         formik.handleReset();
      //         setSubmitting(false);
      //         setOpenNuevoDocumento(false);
      //       })
      //       .catch((e) => dispatch(setError("Error al intentar adjuntar archivo.")))
      //       .finally(() => setSubmitting(false));
      //   }
      // } else {
      //
      //   setSubmitting(false);
      // }
    },

    onReset: () => {
      setAdjuntosTramite([]);
      setIniciadoresSeleccionados([]);
      let filtro = {
        tra_id: props.id,
      };
      api
        .buscarAdjuntosTramites(filtro)
        .then((res) => {
          setAdjuntosTramite(res.data);
        })
        .catch((e) =>
          dispatch(setError("Error al obtener los documentos adjuntos"))
        );
    },
  });

  const validarDocumentoDecision = () => {

    // si no es borrador solo permite PDF
    if (formik.values.idDecision !== "165") {
      if (formatoSeleccionado !== "PDF") {
        // dispatch(
        //   setError(
        //     "El tipo de actuación administrativa seleccionado permite adjuntar solamente documentos en formato PDF."
        //   )
        // );
        return false;
      }
    } else {
      // si es borrador solo permite .txt,.TXT,.doc,.DOC,.docx,.DOCX,.xls,.XLS,.xlsx,.XLSX
      if (
        formatoSeleccionado !== "PDF" &&
        formatoSeleccionado !== "TXT" &&
        formatoSeleccionado !== "DOC" &&
        formatoSeleccionado !== "DOCX" &&
        formatoSeleccionado !== "XLS" &&
        formatoSeleccionado !== "XLSX" &&
        formatoSeleccionado !== "ODT"
      ) {
        dispatch(setError("Formato de documento no admitido."));
        return false;
      }
    }
    return true;
  };

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;

    setIniciadoresSeleccionados(
      // On autofill we get a the stringified value.
      typeof value === "string" ? value.split("; ") : value
    );
  };

  const handleChangeButtonVerde = () => {
    if (props.soporte === 'DIGITAL') {
      setVerifica(true);
    } else {
      setOpenNuevoDocumento(true);
    }

  };

  const handleClose = () => {
    formik.handleReset();
    setOpen(false);
  };

  const handleCloseNuevoDocumento = () => {
    setEditorState(EditorState.createEmpty());
    formik.handleReset();
    setOpenNuevoDocumento(false);
  };

  const handleCloseObservacion = () => {
    setOpenObservacion(false);
  };

  const verDocumento = (adjuntosTramite) => {
    let filtro = {
      id_adjunto: adjuntosTramite.id_adjunto,
    };

    api
      .buscarAdjuntosId(filtro)
      .then((res) => {
        let nombreArchivo = adjuntosTramite.nombre_doc;
        let nombre = nombreArchivo.split(".");
        let extension = nombre[1].toUpperCase();
        if (extension === "PDF") {
          setDocumento(res.data[0].documento);
          setOpen(true);
        } else {
          const url = "data:application/*;base64," + res.data[0].documento;
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", nombreArchivo);
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      })
      .catch((e) => {
        dispatch(setError("Error. No fue posible ubicar el archivo adjunto"));
      });
  };

  const verObservacion = (observacion) => {
    setObservacion(observacion);
    setOpenObservacion(true);
  };

  const buscarDecisionesAdministrativas = () => {
    api
      .listarDecisionesAdministrativas()
      .then((res) => {
        setDecisionesAdministrativas(res.data);
      })
      .catch((e) => {
        dispatch(setError("Error al listar las decisiones administrativas"));
      });
  };

  const buscarIniciadores = () => {
    let filtro = {
      tramiteId: props.id,
    };
    api
      .listarIniciadores(filtro)
      .then((res) => {
        setIniciadores(res.data);
      })
      .catch((e) => {
        dispatch(setError("Error al listar los iniciadores"));
      });
  };

  const handleDelete = (adjunto) => {
    let filtro = {
      id_adjunto: adjunto.id_adjunto,
    };
    api
      .eliminarAdjuntoTramite(filtro)
      .then((res) => {
        dispatch(setSuccess("Archivo adjunto eliminado con éxito"));
        formik.handleReset();
      })
      .catch((e) => {
        dispatch(setError("Error al intentar eliminar el archivo adjunto"));
      });
  };

  const TableCellHead = styled(TableCell)(({ theme }) => ({
    color: "#3c4b64",
  }));

  const buscarAdjuntosTramites = async () => {
    try {
      let filtro = {
        tra_id: props.id,
      };
      let res = await api.buscarAdjuntosTramites(filtro);
      setAdjuntosTramite(res.data);
    } catch (e) {
      console.log("No hay adjuntos para listar");
    }
  };

  const quitarAdjunto = () => {
    formik.setFieldValue("archivoAdjunto", "");
    setNombreArchivoSeleccionado(null);
    setFormatoSeleccionado("");
  };

  useEffect(() => {
    buscarIniciadores();
    console.log(props.expediente);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id]);

  useEffect(() => {
    async function buscarPermisoAgregarAdjuntos() {
      try {
        let filtro = {
          tra_id: props.id,
        };
        let res = await api.buscarPermisoAgregarAdjuntos(filtro);
        setTienePermisoAgregarAdjunto(res.data[0].tiene_permiso);
      } catch (e) {
        dispatch(
          setError(
            "Error al consultar los permisos para agregar documentos adjuntos"
          )
        );
      }
    }
    buscarIniciadores();
    buscarDecisionesAdministrativas();
    buscarAdjuntosTramites();
    buscarPermisoAgregarAdjuntos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id]);

  useEffect(() => {
    async function cargarPlantilla() {
      try {
        console.log(props)
        const plantillaResponse = await api.obtenerPlantilla({ decisionId: formik.values.idDecision });
        const fechaHoy = moment().format('DD/MM/YYYY');
        const fechaExpediente = props.expediente?.fecha_expediente || "";
        const fechaTramite = props.expediente?.fecha_tramite || "";
        const nroExpediente = props.expediente?.nro_expediente || "";
        const nroTramite = props.expediente?.nro_tramite || "";
        const asunto = props.expediente?.asunto || "";

        // Procesar iniciadores
        const iniciadoresLista = iniciadores && iniciadores.length > 0
          ? iniciadores.map(ini => ini.nombre).join('<br/>')
          : '';
        
        const iniciadoresLinea = iniciadores && iniciadores.length > 0
          ? iniciadores.map(ini => ini.nombre).join('; ')
          : '';

        let plantillaInforme = plantillaResponse.data.replaceAll('[[HOY]]', fechaHoy);
        plantillaInforme = plantillaInforme.replaceAll('[[FECHA_EXPEDIENTE]]', fechaExpediente);
        plantillaInforme = plantillaInforme.replaceAll('[[FECHA_TRAMITE]]', fechaTramite);
        plantillaInforme = plantillaInforme.replaceAll('[[NUMERO_EXPEDIENTE]]', nroExpediente);
        plantillaInforme = plantillaInforme.replaceAll('[[NUMERO_TRAMITE]]', nroTramite);
        plantillaInforme = plantillaInforme.replaceAll('[[INICIADORES_LISTA]]', iniciadoresLista);
        plantillaInforme = plantillaInforme.replaceAll('[[INICIADORES_LINEA]]', iniciadoresLinea);
        plantillaInforme = plantillaInforme.replaceAll('[[ASUNTO]]', asunto);

        formik.setFieldValue("informe", plantillaInforme);
        // formik.setFieldValue("informe", template ? template : "");

        const contentDataState = ContentState.createFromBlockArray(convertFromHTML(plantillaResponse.data ? plantillaInforme : ""));
        const editorDataState = EditorState.createWithContent(contentDataState);
        setEditorState(editorDataState);
      } catch (error) {
        dispatch(setError("Error al obtener la plantilla"));
      }
    };

    if (!formik.values.idDecision) return;
    cargarPlantilla();
  }, [formik.values.idDecision]);

  useEffect(() => {
    if (!formik.values.archivoAdjunto) return;

    formik.setFieldValue("informe", "");
    const contentDataState = ContentState.createFromBlockArray(convertFromHTML(""));
    const editorDataState = EditorState.createWithContent(contentDataState);
    setEditorState(editorDataState);
  }, [formik.values.archivoAdjunto]);

  useEffect(() => {
    if (!notEmptyEditor(formik.values.informe)) return;

    quitarAdjunto();

  }, [formik.values.informe]);

  return (
    <Grid color="primary">
      <Grid container sx={{ display: { xs: "none", sm: "block" } }}>
        <Grid item xs={12} align="right" sx={{ marginBottom: 2 }}>
          <Accion funcion={props.funcion} accion="Alta adjunto">
            <Button
              variant="verde"
              onClick={handleChangeButtonVerde}
              endIcon={<AddIcon />}
              disabled={tienePermisoAgregarAdjunto === "S" ? false : true}
            >
              Nuevo
          </Button>
          </Accion>
        </Grid>
      </Grid>
      <Grid container sx={{ display: { xs: "block", sm: "none" } }} align="right">
        <Accion funcion={props.funcion} accion="Alta adjunto">
          <IconButton
            title="Nuevo"
            size="medium"
            sx={{
              color: "#fff",
              backgroundColor: "#799F4F",
              "&:hover": { color: "#fff", backgroundColor: "#546f37" },
              margin: 1,
            }}
            onClick={handleChangeButtonVerde}
            disabled={tienePermisoAgregarAdjunto === "S" ? false : true}
          >
            <AddIcon />
          </IconButton>
        </Accion>
      </Grid>

      <TableContainer>
        <Table
          aria-label="tabla adjuntos"
          sx={{ border: "1px solid #dee2e6" }}
          size="small"

        >
          <TableHead>
            <TableRow>
              <TableCellHead padding="normal">
                <b>Nombre del archivo</b>
              </TableCellHead>
              <TableCellHead padding="normal">
                <b>Organismo que adjuntó</b>
              </TableCellHead>
              <TableCellHead align="center" padding="normal">
                <b>Fecha</b>
              </TableCellHead>
              <TableCellHead align="center" padding="normal">
                <b>Acciones</b>
              </TableCellHead>
            </TableRow>
          </TableHead>
          <TableBody>
            {adjuntosTramite
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((adjunto, index) => {
                return (
                  <TableRow
                    key={index}
                    sx={
                      index % 2 === 0
                        ? { backgroundColor: "#f2f2f2" }
                        : { backgroundColor: "#fff" }
                    }
                  >
                    <TableCell align="left" width="200">
                      {adjunto.nombre_doc}
                    </TableCell>
                    <TableCell align="left" width="300">
                      {adjunto.organismo}
                    </TableCell>
                    <TableCell align="center" width="100">
                      {adjunto.fecha_adjunto}
                    </TableCell>
                    <TableCell align="center" width="200">
                      <>
                        <IconButton
                          title="Ver Observaciones"
                          size="small"
                          onClick={() => verObservacion(adjunto.observacion)}
                          disabled={
                            adjunto.observacion !== null &&
                              adjunto.observacion !== ""
                              ? false
                              : true
                          }
                        >
                          <CommentIcon fontSize="small" />
                        </IconButton>
                        <Accion funcion={props.funcion} accion="Descargar">
                          <IconButton
                            title="Descargar documento"
                            onClick={() => verDocumento(adjunto)}
                            size="small"
                            disabled={adjunto.id_adjunto !== null ? false : true}
                          >
                            <FileDownloadOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Accion>
                        <Accion funcion={props.funcion} accion="Firma remota">
                          <FirmaRemota
                            adjuntoId={adjunto.id_adjunto}
                            nombreAdjunto={adjunto.nombre_doc}
                            handleFirmado={buscarAdjuntosTramites}
                            disabled={
                              !adjunto.nombre_doc
                                .toLowerCase()
                                .includes(".pdf") ||
                              adjunto.es_documento === "N" ||
                              tienePermisoAgregarAdjunto === "N"
                            }
                          />
                        </Accion>
                        <Accion funcion={props.funcion} accion="Firma con TOKEN">
                          <FirmaToken
                            adjuntoId={adjunto.id_adjunto}
                            nombreAdjunto={adjunto.nombre_doc}
                            handleFirmado={buscarAdjuntosTramites}
                            disabled={
                              !adjunto.nombre_doc
                                .toLowerCase()
                                .includes(".pdf") ||
                              adjunto.es_documento === "N" ||
                              tienePermisoAgregarAdjunto === "N"
                            }
                          />
                        </Accion>
                        <Accion funcion={props.funcion} accion="Verificar firma">
                          <VerificarFirma
                            adjuntoId={adjunto.id_adjunto}
                            nombreAdjunto={adjunto.nombre_doc}
                            disabled={
                              !adjunto.nombre_doc
                                .toLowerCase()
                                .includes(".pdf") ||
                              adjunto.es_documento === "N"
                            }
                          />
                        </Accion>
                        <Accion funcion={props.funcion} accion="eliminar">
                          <ModalNotificar
                            id={adjunto.tra_id}
                            adjuntoId={adjunto.id_adjunto}
                            nombreAdjunto={adjunto.nombre_doc}
                            disabled={
                              adjunto.es_documento === "N" ||
                              adjunto.mom_id == 165 ||
                              tienePermisoAgregarAdjunto !== "S"
                            }
                            iniciadores={iniciadores}
                            actualizarDatos={actualizarDatos}
                          />
                        </Accion>
                        <Accion funcion={props.funcion} accion="eliminar">
                          {adjunto.eliminar === "S" ? (
                            <DeleteDialog
                              size="small"
                              handleDelete={handleDelete}
                              data={adjunto}
                            />
                          ) : (

                              <IconButton
                                title="Eliminar documento"
                                size="small"
                                disabled
                              >
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                              </IconButton>
                            )}
                        </Accion>
                      </>
                    </TableCell>
                  </TableRow>
                );
              })}
            {adjuntosTramite.length === 0 ? (
              <TableRow>
                <TableCell colSpan="6" align="center">
                  <Typography variant="aclaracion">Sin adjuntos</Typography>
                </TableCell>
              </TableRow>
            ) : (
                ""
              )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, { label: "Todos", value: adjuntosTramite.length }]}
                funcion={props.funcion}
                count={adjuntosTramite.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      <Modal open={open} handleClose={handleClose} closeButton>
        <VisorPdf url={"data:application/octet-stream;base64," + documento} />
      </Modal>

      <Modal open={openNuevoDocumento} handleClose={handleCloseNuevoDocumento}>
        <form
          id="formularioAgregarAdjunto"
          name="formularioAgregarAdjunto"
          onSubmit={formik.handleSubmit}
          onReset={formik.handleReset}
          noValidate
          autoComplete="off"
        >
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} sx={{ marginBottom: 1.5 }}>
              <Typography variant="titulo">ADJUNTAR DOCUMENTO</Typography>
            </Grid>
          </Grid>

          <Grid container spacing={1} sx={{ marginBottom: 1 }}>
            <Grid item xs={12} sm={12} md={6}>
              <FormControl
                variant="outlined"
                fullWidth
                size="small"
                sx={{ paddingLeft: 0, margin: 0 }}
                error={
                  formik.touched.idDecision && Boolean(formik.errors.idDecision)
                }
              >
                <InputLabel id="idDecision">Actuación administrativa</InputLabel>
                <Select
                  labelId="idDecisionLabel"
                  id="idDecision"
                  name="idDecision"
                  label="Actuación administrativa"
                  variant="outlined"
                  value={formik.values.idDecision}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="" selected disabled>
                    Seleccione
                  </MenuItem>
                  {decisionesAdministrativas.map((decision) => (
                    <MenuItem value={decision.id} key={decision.id}>
                      {decision.descripcion}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {formik.touched.idDecision && formik.errors.idDecision}
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={12}>
              <Typography variant="aclaracion">
                {"Podés armar tu informe desde el editor o podés adjuntarlo en formato PDF"}
              </Typography>
              <Editor
                editorState={editorState}
                onEditorStateChange={onEditorStateChange}
                wrapperClassName="wrapperClassName"
                toolbar={{
                  options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'history'],
                  inline: {
                    options: ['bold', 'italic', 'underline', 'strikethrough'],
                  },

                }}
                toolbarStyle={{ border: '1px solid #f1f1f1' }}
                editorStyle={{
                  boxSizing: 'border-box',
                  border: `1px solid ${formik.touched.informe && Boolean(formik.errors.informe) ? "red" : "#CCCCCC"}`,
                  padding: '5px',
                  borderRadius: '.25rem',
                  height: '200px',
                }}
                readOnly={!formik.values.idDecision}
              />
              <FormHelperText error={formik.touched.informe && Boolean(formik.errors.informe)}>
                {formik.touched.informe && formik.errors.informe}
              </FormHelperText>
            </Grid>
          </Grid>

          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={12}>
              <FileInput
                id="archivoDocumento"
                name="archivoDocumento"
                label="Adjuntar documento"
                variant="outlined"
                size="small"
                fullWidth
                disabled={!formik.values.idDecision}
                onChange={(event) => {
                  formik.setFieldValue(
                    "archivoAdjunto",
                    event.target.files[0]
                  );
                  definirFormato(event);
                }}
                value={formik.values.archivoAdjunto}
                clear={quitarAdjunto}
                error={
                  formik.touched.archivoAdjunto &&
                  Boolean(formik.errors.archivoAdjunto)
                }

                helperText={
                  formik.touched.archivoAdjunto && formik.errors.archivoAdjunto
                }
              />
              <Typography variant="aclaracion" color="#FFA500">
                {"Recordá que si elegís la opción Adjuntar documento se borra lo que escribiste en el editor"}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={12}>
              <TextField
                id="observacion"
                name="observacion"
                label="Observaciones"
                multiline
                size="small"
                rows={4}
                variant="outlined"
                fullWidth
                inputProps={{ maxLength: 2000 }}
                value={formik.values.observacion}
                onChange={formik.handleChange}
                error={
                  formik.touched.observacion &&
                  Boolean(formik.errors.observacion)
                }
                helperText={
                  formik.touched.observacion && formik.errors.observacion
                }
                disabled={!formik.values.idDecision}
              />
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12}>
              <FormControl
                variant="outlined"
                size="small"
                margin="dense"
                error={
                  formik.touched.notificar &&
                  Boolean(formik.errors.notificar)
                }
                fullWidth
              >
                <InputLabel id="iniciadores">Notificar a...</InputLabel>
                <Select
                  labelId="iniciadores"
                  id="iniciadores"
                  multiple
                  variant="outlined"
                  value={iniciadoresSeleccionados}
                  onChange={handleChange}
                  input={<OutlinedInput label="Notificar a ..." />}
                  renderValue={(selected) => selected.join("; ")}
                  MenuProps={MenuProps}
                  disabled={!formik.values.idDecision}
                >
                  {iniciadores.map((iniciador) => (
                    <MenuItem
                      key={iniciador.id}
                      value={
                        iniciador.email !== null
                          ? iniciador.nombre + " (" + iniciador.email + ")"
                          : iniciador.nombre
                      }
                      disabled={iniciador.email === null}
                    >
                      <Checkbox
                        checked={
                          iniciadoresSeleccionados.indexOf(
                            iniciador.email !== null
                              ? iniciador.nombre + " (" + iniciador.email + ")"
                              : iniciador.nombre
                          ) > -1
                        }
                      />
                      <ListItemText>
                        {iniciador.email !== null
                          ? iniciador.nombre + " (" + iniciador.email + ")"
                          : iniciador.nombre}
                      </ListItemText>
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {formik.touched.iniciadores && formik.errors.iniciadores}
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={1}>
            <Grid
              item
              xs={12}
              align="right"
              sx={{ display: { xs: "none", md: "inline" } }}
            >
              <Button
                variant="plata"
                onClick={handleCloseNuevoDocumento}
                endIcon={<ClearIcon />}
                sx={{ m: 1 }}
              >
                {"Cancelar"}
              </Button>{" "}
              <Button
                sx={{ m: 1 }}
                variant="verde"
                color="primary"
                type="submit"
                disabled={submitting}
                endIcon={<DoneOutlineRounded />}
              >
                {submitting ? "Confirmando..." : "Confirmar"}
              </Button>
            </Grid>
            <Grid
              item
              xs={12}
              align="right"
              sx={{ display: { xs: "block", md: "none" } }}
            >
              <IconButton color="primary" onClick={handleCloseNuevoDocumento} title="Cancelar">
                <ClearIcon />
              </IconButton>{" "}
              <IconButton
                color="primary"
                type="submit"
                disabled={submitting}
                title="Confirmar"
              >
                <DoneOutlineRounded />
              </IconButton>
            </Grid>
            <Grid item xs={12} sm={12}>
              {" "}
            </Grid>
          </Grid>
        </form>
      </Modal>

      <Modal
        open={openObservacion}
        handleClose={handleCloseObservacion}
        size="sm"
      >
        <Typography variant="titulo" paragraph>
          OBSERVACION
        </Typography>
        <Typography variant="cuerpo" paragraph>
          {observacion}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row-reverse",
          }}
        >
          <Button
            sx={{ m: 1 }}
            variant="plata"
            onClick={handleCloseObservacion}
          >
            {"Cerrar"}
          </Button>
        </Box>
      </Modal>
      {verifica ? (
        <VerificarUltimoAdjunto
          tramiteId={props.id}
          verficarElFirmado={verficarElFirmado}
          verficarElFirmadoCancelar={verficarElFirmadoCancelar}
        >
        </VerificarUltimoAdjunto>) : (null)}
    </Grid>
  );
};

export default Adjuntos;
